import { IdTokenResult } from 'firebase/auth';
import { useEffect, useState } from 'react';

import { useUserState } from '@/state';

export type Permission = 'all' | 'editor' | 'roomServiceManager' | 'rentalManager' | 'reportManager' | 'spaManager' | 'storeManager';
export type StaffServicePermission = 'reports' | 'roomservice' | 'store' | 'rental' | 'qrscanner' | 'rooms';

interface CustomClaims {
  role?: string[];
  permissions?: Permission[];
  staffService?: boolean;
  staffServicePermissions?: StaffServicePermission[];
  /** Flaga personelu nadawana skryptem/backendem (claim `isStaff`). */
  isStaff?: boolean;
  /** Administrator HotelSpot — ten sam warunek co w backendowym `verifyAdminAccess`. */
  isAdmin?: boolean;
  isHotelspotAdmin?: boolean;
  /**
   * Organizacje, których konto jest właścicielem. ACS ustawia TABLICĘ id
   * (konto może mieć kilka obiektów), starsze konta bywają pojedynczym id.
   */
  owner?: string[] | string;
}

/**
 * Przepisanie claimów z tokenu na nasz kształt.
 *
 * JEDNO miejsce, bo odczyt dzieje się dwa razy — przy starcie i przy
 * `refreshClaims`. Wcześniej były to dwie kopie tej samej listy pól i obie
 * rozjechały się z `CustomClaims`: `isAdmin`, `isHotelspotAdmin` i `owner`
 * były zadeklarowane w typie i czytane niżej, ale nigdy nie trafiały do stanu.
 * Efekt: konto właściciela albo administratora HotelSpot nie było w panelu
 * rozpoznawane — mimo poprawnych claimów w tokenie.
 */
const readClaims = (idTokenResult: IdTokenResult): CustomClaims => ({
  role: idTokenResult.claims.role as string[] | undefined,
  permissions: idTokenResult.claims.permissions as Permission[] | undefined,
  staffService: idTokenResult.claims.staffService as boolean | undefined,
  staffServicePermissions: idTokenResult.claims.staffServicePermissions as StaffServicePermission[] | undefined,
  isStaff: idTokenResult.claims.isStaff as boolean | undefined,
  isAdmin: idTokenResult.claims.isAdmin as boolean | undefined,
  isHotelspotAdmin: idTokenResult.claims.isHotelspotAdmin as boolean | undefined,
  owner: idTokenResult.claims.owner as string[] | string | undefined,
});

/**
 * Hook to check user permissions from Firebase custom claims
 * Permissions are stored in custom claims and verified server-side
 *
 * IMPORTANT: This requires the user to refresh their ID token after permissions change
 * Custom claims are read from the Firebase Auth token
 */
export const usePermissions = () => {
  const { currentUser } = useUserState();
  const [customClaims, setCustomClaims] = useState<CustomClaims>({});

  useEffect(() => {
    const fetchClaims = async () => {
      if (!currentUser) {
        setCustomClaims({});
        return;
      }

      try {
        const idTokenResult = await currentUser.getIdTokenResult();
        setCustomClaims(readClaims(idTokenResult));
      } catch (error) {
        setCustomClaims({});
      }
    };

    fetchClaims();

    // Refresh claims every 5 minutes or when user changes
    const interval = setInterval(fetchClaims, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [currentUser]);

  const permissions = customClaims.permissions || [];
  const roles = customClaims.role || [];
  const staffService = customClaims.staffService || false;
  const staffServicePermissions = customClaims.staffServicePermissions || [];

  const hasPermission = (permission: Permission): boolean => {
    if (permissions.includes('all')) {
      return true;
    }

    return permissions.includes(permission);
  };

  const hasAnyPermission = (requiredPermissions: Permission[]): boolean => requiredPermissions.some((permission) => hasPermission(permission));

  const hasAllPermissions = (requiredPermissions: Permission[]): boolean => requiredPermissions.every((permission) => hasPermission(permission));

  // Właściciel: rola `owner` (samodzielna rejestracja panelu) ALBO claim `owner`
  // z ACS, który niesie id organizacji. Bez tego drugiego konto oznaczone jako
  // właściciel w ACS nie było w panelu rozpoznawane wcale.
  const isOwnerOf = (organizationId?: string | null): boolean => {
    if ((customClaims.role || []).includes('owner')) return true;

    const claimOwner = customClaims.owner;
    if (!organizationId || !claimOwner) return false;
    return Array.isArray(claimOwner)
      ? claimOwner.includes(organizationId)
      : claimOwner === organizationId;
  };

  return {
    permissions,
    roles,
    staffService,
    staffServicePermissions,
    isStaff: customClaims.isStaff === true,
    // Administrator HotelSpot — ten sam warunek, co w backendzie
    // (`verifyAdminAccess`): claim `isAdmin` albo `isHotelspotAdmin`.
    isAdmin: customClaims.isAdmin === true || customClaims.isHotelspotAdmin === true,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isOwner: isOwnerOf(),
    /** Właściciel KONKRETNEJ organizacji — po id z `organizationState`. */
    isOwnerOf,
    refreshClaims: async () => {
      if (currentUser) {
        await currentUser.getIdToken(true); // Force refresh
        const idTokenResult = await currentUser.getIdTokenResult();
        setCustomClaims(readClaims(idTokenResult));
      }
    },
  };
};
